import { useSearchParams } from 'react-router';
import { ReadOnlyNote } from '../../../components/Form';
import { PageHead } from '../../../components/PageHead';
import { ErrorBox, Skeleton } from '../../../components/States';
import { useIsAdmin } from '../../../lib/session';
import { useSettings } from '../../../lib/settings-api';
import { BusinessTab } from './BusinessTab';
import { CostsTab } from './CostsTab';
import { NumberingTab } from './NumberingTab';
import { RulesTab } from './RulesTab';
import { TemplatesTab } from './TemplatesTab';

const TABS = [
  ['negocio', 'Negócio'],
  ['custos', 'Custos'],
  ['regras', 'Regras de preço'],
  ['modelos', 'Modelos de mensagem'],
  ['numeracao', 'Numeração'],
] as const;
type Tab = (typeof TABS)[number][0];

/** Configurações · Negócio e custos (design Config-Custos). */
export function SettingsPage() {
  const isAdmin = useIsAdmin();
  const readOnly = !isAdmin;
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find(([t]) => t === params.get('aba'))?.[0] ?? 'custos';
  const settings = useSettings();

  return (
    <>
      <PageHead
        crumb="Configurações"
        title="Negócio e custos"
        actions={readOnly ? <span className="badge info">Vendo como Operadora</span> : undefined}
      />
      <div className="tabs" aria-label="Seções" role="tablist">
        {TABS.map(([t, label]) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={tab === t ? 'on' : ''}
            onClick={() => setParams({ aba: t }, { replace: true })}
          >
            {label}
          </button>
        ))}
      </div>
      {readOnly ? (
        <ReadOnlyNote>
          <b>Só Admin altera.</b> Você entrou como Operadora: pode ver todos os valores desta
          página, mas não editar. Peça ao Admin se algo precisar mudar.
        </ReadOnlyNote>
      ) : null}
      {settings.isPending ? <Skeleton rows={3} height={120} /> : null}
      {settings.isError ? (
        <ErrorBox
          title="Não foi possível carregar as configurações"
          onRetry={() => void settings.refetch()}
        />
      ) : null}
      {settings.data ? (
        <>
          {tab === 'negocio' ? (
            <BusinessTab
              key={settings.data.updatedAt}
              settings={settings.data}
              readOnly={readOnly}
            />
          ) : null}
          {tab === 'custos' ? (
            <CostsTab key={settings.data.updatedAt} settings={settings.data} readOnly={readOnly} />
          ) : null}
          {tab === 'regras' ? (
            <RulesTab key={settings.data.updatedAt} settings={settings.data} readOnly={readOnly} />
          ) : null}
          {tab === 'modelos' ? <TemplatesTab readOnly={readOnly} /> : null}
          {tab === 'numeracao' ? <NumberingTab readOnly={readOnly} /> : null}
        </>
      ) : null}
    </>
  );
}
